/**
 * Dane masowe dla Mercedes Benz Tourist Line (bez HighDeck), zależne od kodu
 * wersji pojazdu ({{version}}) - patrz iveco-mass-data.ts i
 * mercedes-tourist-line-highdeck-mass-data.ts po strukturę i konwencje.
 *
 * Źródło: fabryczna tabela Mercedes "2.6. Mass in running order" dla podwozia
 * Sprintera (tourist-line-table.html - PDF wyeksportowany do tekstu, część
 * kolumn nieczytelna/rozjechana przy kopiowaniu, stąd ręczna rekonstrukcja
 * poniżej). Tabela źródłowa ma wiersze kluczowane 2-cyfrowym kodem + literą
 * klasy masy (A/B/C/D/F) + literą wariantu (W/P/Z/N).
 *
 * WAŻNE: {{versionPassengersCode}} jest polem tekstowym - użytkownik może
 * wpisać dowolny fabryczny kod, nie tylko te, które dziś automatycznie
 * wylicza getMercedesTouristLinePassengersCode() (08/10/18/19/37). Dlatego
 * poniżej są przepisane WSZYSTKIE wiersze źródła dla klas masy "C" i "F"
 * (jedyne dwie opcje pola {{versionMassCode}} - klasy "A", "B", "D" ze
 * źródła są poza zakresem tego pola i zostały pominięte).
 *
 * Klucze są przepisane DOSŁOWNIE z fabrycznej litery wariantu na końcu kodu -
 * W (zakres/kilka wariantów siedzeń na tym samym certyfikacie masy), P
 * (pojedynczy stały układ siedzeń) albo Z (wersja z wózkiem inwalidzkim).
 * To NIE jest to samo "W"/"P", co dziś zwraca getMercedesTouristLineWheelchairCode()
 * (tam W/P oznacza wyłącznie "z wózkiem"/"bez wózka") - wersje z wózkiem mają
 * tu fabrycznie literę "Z" (np. "37CZ", nie "37CW"), więc dopóki logika kodu
 * pasażerów/wózków nie zostanie dostosowana do tego rozjazdu (do zrobienia
 * później), automatycznie wyliczony klucz {{version}} dla kodu "37" nie
 * trafi w wiersz "37CZ"/"37FZ" poniżej - to świadomy, tymczasowy stan, nie
 * błąd w danych.
 *
 * Tak jak dla Iveco/MB HighDeck, wyliczane są wyłącznie 3 wartości (Sekcja 13):
 * - {{massInRunningOrder}} = kolumna "2.6" (wartość max)
 * - {{massAxle2}}          = kolumna "Axle 2" (wartość max)
 * - {{massAxle1}}          = {{massInRunningOrder}} - {{massAxle2}} (wyliczane)
 * Wartości min są przechowywane wyłącznie jako zakres referencyjny (nie są
 * wpisywane do formularza). Kolumny "Volume of luggage"/"Max. mass of
 * luggage" (13.7/2.12 w źródle) nie są tu potrzebne i zostały pominięte -
 * tak jak w pozostałych tabelach tego folderu.
 *
 * Sprinter (w przeciwieństwie do Iveco/MB HighDeck) nie ma osobnej kolumny
 * liczby stojących pasażerów - to minibus, wyłącznie miejsca siedzące - każdy
 * kod wersji jest już jednoznaczny, dlatego wszystkie wpisy są pod kluczem "*".
 *
 * Klasa "F" ma w źródle mniej kodów niż "C" (np. brak "05FW"/"06FP"/"09FW"/
 * "10FP"/"23FP"/"26FP"/"32FP"/"33FP") - to fakt danych (fabryka nie oferuje
 * tych kombinacji dla tej klasy masy), nie luka do uzupełnienia.
 *
 * @module mercedes-tourist-line-mass-data
 */

import type { VersionMassDataTable } from "./vehicle-mass-data";

export const MERCEDES_TOURIST_LINE_MASS_DATA: VersionMassDataTable = {
  // ---- Klasa masy C (MASA 5500) ----
  // Seats 19/15/12, wheelchairs 0/1/2
  "01CW": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1700,
      axle2Min: 2000,
      axle2Max: 2300,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 4000,
    },
  },
  // Seats 19+1*, wheelchairs 0
  "02CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1660,
      axle2Min: 2000,
      axle2Max: 2250,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 3910,
    },
  },
  "03CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1660,
      axle2Min: 2000,
      axle2Max: 2250,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 3910,
    },
  },
  "04CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1660,
      axle2Min: 2000,
      axle2Max: 2250,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 3910,
    },
  },
  // Seats 16/14/12+1*, wheelchairs 0/1/2
  "05CW": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1700,
      axle2Min: 2000,
      axle2Max: 2300,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 4000,
    },
  },
  // Seats 16+1*, wheelchairs 0
  "06CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1700,
      axle2Min: 2000,
      axle2Max: 2300,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 4000,
    },
  },
  // Seats 19/15/12+1*, wheelchairs 0/1/2
  "07CW": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1700,
      axle2Min: 2000,
      axle2Max: 2300,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 4000,
    },
  },
  // Seats 19+1*, wheelchairs 0
  "08CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1650,
      axle2Min: 2000,
      axle2Max: 2250,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 3900,
    },
  },
  // Seats 16/14/12, wheelchairs 0/1/2
  "09CW": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1700,
      axle2Min: 2000,
      axle2Max: 2300,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 4000,
    },
  },
  // Seats 16+1*, wheelchairs 0
  "10CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1700,
      axle2Min: 2000,
      axle2Max: 2300,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 4000,
    },
  },
  // Seats 19, wheelchairs 0
  "11CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1690,
      axle2Min: 2000,
      axle2Max: 2250,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 3940,
    },
  },
  "12CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1700,
      axle2Min: 2000,
      axle2Max: 2300,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 4000,
    },
  },
  // Seats 18, wheelchairs 0
  "15CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1700,
      axle2Min: 2000,
      axle2Max: 2300,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 4000,
    },
  },
  "16CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1700,
      axle2Min: 2000,
      axle2Max: 2300,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 4000,
    },
  },
  // Seats 19, wheelchairs 0
  "17CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1600,
      axle2Min: 2000,
      axle2Max: 2250,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 3850,
    },
  },
  // Seats 19+1*, wheelchairs 0
  "18CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1600,
      axle2Min: 2000,
      axle2Max: 2250,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 3850,
    },
  },
  // Seats 22+1*, wheelchairs 0
  "19CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1660,
      axle2Min: 2000,
      axle2Max: 2100,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 3760,
    },
  },
  "20CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1660,
      axle2Min: 2000,
      axle2Max: 2100,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 3760,
    },
  },
  // Seats 19, wheelchairs 0
  "21CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1700,
      axle2Min: 2000,
      axle2Max: 2150,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 3850,
    },
  },
  // Seats 12+1*, wheelchairs 0
  "23CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1674,
      axle2Min: 2000,
      axle2Max: 2598,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 4272,
    },
  },
  // Seats 16+1*, wheelchairs 0
  "26CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1651,
      axle2Min: 2000,
      axle2Max: 2378,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 4029,
    },
  },
  // Seats 19+1*, wheelchairs 0
  "27CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1613,
      axle2Min: 2000,
      axle2Max: 2163,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 3776,
    },
  },
  // Seats 19, wheelchairs 0
  "28CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1666,
      axle2Min: 2000,
      axle2Max: 2185,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 3851,
    },
  },
  "29CP": {
    "*": {
      axle1RangeMin: 1375,
      axle1RangeMax: 1489,
      axle2Min: 2000,
      axle2Max: 2578,
      massInRunningOrderMin: 3375,
      massInRunningOrderMax: 4076,
    },
  },
  // Seats 19/15/12/8/6, wheelchairs 0/1/2/3/4
  "30CW": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1702,
      axle2Min: 1900,
      axle2Max: 2467,
      massInRunningOrderMin: 3602,
      massInRunningOrderMax: 4101,
    },
  },
  // Seats 16+1*, wheelchairs 0
  "32CP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1673,
      axle2Min: 1934,
      axle2Max: 2536,
      massInRunningOrderMin: 3607,
      massInRunningOrderMax: 4106,
    },
  },
  "33CP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1778,
      axle2Min: 1900,
      axle2Max: 2381,
      massInRunningOrderMin: 3560,
      massInRunningOrderMax: 4059,
    },
  },
  // Seats 22/18/15/7/3, wheelchairs 0/1/2/4/6
  "34CW": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1825,
      axle2Min: 1900,
      axle2Max: 2172,
      massInRunningOrderMin: 3389,
      massInRunningOrderMax: 3888,
    },
  },
  // Wersje z wózkiem inwalidzkim (fabrycznie sufiks "Z")
  // Seats 16+1*/14+1*/12+1*, wheelchairs 0/1/2
  "36CZ": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1782,
      axle2Min: 1900,
      axle2Max: 2393,
      massInRunningOrderMin: 3200,
      massInRunningOrderMax: 4011,
    },
  },
  // Seats 16+1*/14+1*, wheelchairs 0/1
  "37CZ": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1835,
      axle2Min: 1900,
      axle2Max: 2686,
      massInRunningOrderMin: 3200,
      massInRunningOrderMax: 4221,
    },
  },
  // Seats 19+1*/15+1*, wheelchairs 0/1
  "38CZ": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1706,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3200,
      massInRunningOrderMax: 4076,
    },
  },
  // Seats 19+1*/17+1*/15+1*, wheelchairs 0/1/2
  "39CZ": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1909,
      axle2Min: 1900,
      axle2Max: 2304,
      massInRunningOrderMin: 3200,
      massInRunningOrderMax: 3940,
    },
  },

  // ---- Klasa masy F (MASA 6100) ----
  // Seats 19/15/12, wheelchairs 0/1/2
  "01FW": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1700,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4084,
    },
  },
  // Seats 19+1*, wheelchairs 0
  "02FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1660,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4050,
    },
  },
  "03FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1660,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4050,
    },
  },
  "04FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1660,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4050,
    },
  },
  // Seats 19/15/12+1*, wheelchairs 0/1/2
  "07FW": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1700,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4050,
    },
  },
  // Seats 19+1*, wheelchairs 0
  "08FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1650,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4050,
    },
  },
  // Seats 19, wheelchairs 0
  "11FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1690,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4084,
    },
  },
  "12FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1700,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4084,
    },
  },
  // Seats 18, wheelchairs 0
  "15FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1700,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4118,
    },
  },
  "16FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1700,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4118,
    },
  },
  // Seats 19, wheelchairs 0
  "17FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1600,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4084,
    },
  },
  // Seats 19+1*, wheelchairs 0
  "18FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1600,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4050,
    },
  },
  // Seats 22+1*, wheelchairs 0
  "19FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1660,
      axle2Min: 1900,
      axle2Max: 2371,
      massInRunningOrderMin: 3259,
      massInRunningOrderMax: 3948,
    },
  },
  "20FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1660,
      axle2Min: 1900,
      axle2Max: 2371,
      massInRunningOrderMin: 3259,
      massInRunningOrderMax: 3948,
    },
  },
  // Seats 19, wheelchairs 0
  "21FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1700,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4084,
    },
  },
  // Seats 19+1*, wheelchairs 0
  "27FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1613,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4050,
    },
  },
  // Seats 19, wheelchairs 0
  "28FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1666,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4084,
    },
  },
  "29FP": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1600,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3372,
      massInRunningOrderMax: 4084,
    },
  },
  // Seats 19/15/12/8/6, wheelchairs 0/1/2/3/4
  "30FW": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1702,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3602,
      massInRunningOrderMax: 4140,
    },
  },
  // Seats 22/18/15/7/3, wheelchairs 0/1/2/4/6
  "34FW": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1825,
      axle2Min: 1900,
      axle2Max: 2371,
      massInRunningOrderMin: 3389,
      massInRunningOrderMax: 4004,
    },
  },
  // Wersje z wózkiem inwalidzkim (fabrycznie sufiks "Z")
  // Seats 16+1*/14+1*/12+1*, wheelchairs 0/1/2
  "36FZ": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1818,
      axle2Min: 1900,
      axle2Max: 2793,
      massInRunningOrderMin: 3200,
      massInRunningOrderMax: 4611,
    },
  },
  // Seats 16+1*/14+1*, wheelchairs 0/1
  "37FZ": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1835,
      axle2Min: 1900,
      axle2Max: 3086,
      massInRunningOrderMin: 3200,
      massInRunningOrderMax: 4881,
    },
  },
  // Seats 19+1*/15+1*, wheelchairs 0/1
  "38FZ": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1706,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3200,
      massInRunningOrderMax: 4140,
    },
  },
  // Seats 19+1*/17+1*/15+1*, wheelchairs 0/1/2
  "39FZ": {
    "*": {
      axle1RangeMin: 1300,
      axle1RangeMax: 1909,
      axle2Min: 1900,
      axle2Max: 2540,
      massInRunningOrderMin: 3200,
      massInRunningOrderMax: 4140,
    },
  },
};
